import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "@/lib/toast";
import { Card, Icon } from "@/components/primitives";

type Meas = { pit: string; length: string; shoulder: string; sleeve: string; waist: string; hip: string };
const EMPTY_MEAS: Meas = { pit: "", length: "", shoulder: "", sleeve: "", waist: "", hip: "" };
const measKey = (id: string | null) => `fig.listing.meas.${id ?? "pending"}`;

interface VerificationLike {
  tier?: "photo_verified" | "self_reported" | "uncertain";
}

interface ItemLike {
  title?: string;
  brand?: string;
  era?: string;
  category?: string;
  material?: string;
  size?: string;
  color?: string;
  condition?: string;
  conditionNotes?: string;
  rarity?: string;
  notes?: string[];
  tags?: string[];
  pricing: { suggested: number; low: number; high: number; platforms?: { name: string }[] };
}

/**
 * Self-contained "Prepare marketplace listing" dropdown — moved out of
 * Result.tsx so the Price page (Open Market Report) can host the same flow.
 * State, persistence, draft generation, copy/export all live here.
 */
export function PrepareListing({
  item,
  verification,
  savedScanId,
  defaultOpen = false,
}: {
  item: ItemLike;
  verification: VerificationLike | null;
  savedScanId: string | null;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [market, setMarket] = useState("");
  const [price, setPrice] = useState("");
  const [meas, setMeas] = useState<Meas>(() => {
    try {
      const raw = localStorage.getItem(measKey(null));
      return raw ? { ...EMPTY_MEAS, ...JSON.parse(raw) } : EMPTY_MEAS;
    } catch { return EMPTY_MEAS; }
  });

  const marketOptions = useMemo<string[]>(() => {
    const fromPlatforms = (item.pricing.platforms ?? []).map((p) => p.name).filter(Boolean);
    const fallback = ["Depop", "Grailed", "eBay", "Poshmark", "Vestiaire", "Etsy"];
    return Array.from(new Set([...fromPlatforms, ...fallback]));
  }, [item.pricing.platforms]);

  // Rehydrate measurements per scan id (mirrors Result.tsx behavior).
  const prevScanIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!savedScanId) return;
    const wasNull = prevScanIdRef.current === null;
    prevScanIdRef.current = savedScanId;
    try {
      const scoped = localStorage.getItem(measKey(savedScanId));
      if (scoped) { setMeas({ ...EMPTY_MEAS, ...JSON.parse(scoped) }); return; }
      if (wasNull) {
        const pending = localStorage.getItem(measKey(null));
        if (pending) {
          localStorage.setItem(measKey(savedScanId), pending);
          localStorage.removeItem(measKey(null));
          setMeas({ ...EMPTY_MEAS, ...JSON.parse(pending) });
          return;
        }
      }
      setMeas(EMPTY_MEAS);
    } catch { setMeas(EMPTY_MEAS); }
  }, [savedScanId]);

  useEffect(() => {
    try { localStorage.setItem(measKey(savedScanId), JSON.stringify(meas)); } catch { /* quota */ }
  }, [meas, savedScanId]);

  useEffect(() => {
    if (open) {
      if (!price) setPrice(String(item.pricing.suggested ?? ""));
      if (!market) setMarket(marketOptions[0] ?? "");
    }
  }, [open, item.pricing.suggested, marketOptions, price, market]);

  const generatedListing = useMemo(() => {
    const priceNum = Number(price);
    const priceStr = Number.isFinite(priceNum) && priceNum > 0 ? `$${priceNum}` : "$—";
    const story = (item.notes || []).slice(0, 2).join(" ");
    const verifLine =
      verification?.tier === "photo_verified" ? "Verified by Fig — photo-checked." :
      verification?.tier === "uncertain"      ? "Material unverified — sold as-is." :
      verification?.tier === "self_reported"  ? "Self-reported by seller." : "";
    const m = (market || "").toLowerCase();

    const formatMeas = (raw: string): string | null => {
      const cleaned = raw.replace(/[”"]/g, "").replace(/\b(in|ins|inch|inches|cm|mm)\b/gi, "").trim();
      if (!cleaned) return null;
      const num = Number(cleaned);
      if (Number.isFinite(num) && num > 0) {
        const pretty = Number.isInteger(num) ? String(num) : String(parseFloat(num.toFixed(2)));
        return `${pretty}"`;
      }
      return `${cleaned.replace(/"+$/, "")}"`;
    };

    const MEAS_LABELS: Array<[keyof Meas, string]> = [
      ["pit", "Pit-to-pit"], ["length", "Length"], ["shoulder", "Shoulder"],
      ["sleeve", "Sleeve"], ["waist", "Waist"], ["hip", "Hip"],
    ];
    const filledMeas = MEAS_LABELS
      .map(([k, label]) => [label, formatMeas(meas[k])] as const)
      .filter((row): row is readonly [string, string] => row[1] !== null);
    const labelWidth = filledMeas.reduce((mx, [k]) => Math.max(mx, k.length), 0);
    const measBlock = filledMeas.length
      ? filledMeas.map(([k, v]) => `• ${k.padEnd(labelWidth, " ")} : ${v}`).join("\n")
      : "";

    const titleDepop   = [item.era, item.brand, item.color, item.title].filter(Boolean).join(" ");
    const titleGrailed = [item.brand, item.era, item.title, item.size && `Size ${item.size}`].filter(Boolean).join(" ");
    const titleEbay    = [item.brand, item.title, item.era, item.color, item.material, item.size && `Size ${item.size}`].filter(Boolean).join(" ").slice(0, 80);
    const titlePosh    = [item.brand, item.color, item.title, item.size && `Sz ${item.size}`].filter(Boolean).join(" ");
    const titleVest    = [item.brand, item.title, item.material, item.color].filter(Boolean).join(" ");
    const titleEtsy    = [item.era, item.material, item.color, item.title, "Vintage"].filter(Boolean).join(" ");
    const titleDefault = [item.brand, item.era, item.title].filter(Boolean).join(" · ");

    const tagsHash  = (item.tags || []).slice(0, 8).map((t) => `#${t.replace(/\s+/g, "")}`).join(" ");
    const tagsComma = (item.tags || []).slice(0, 13).join(", ");

    const detailsList = [
      item.brand     && `• Brand: ${item.brand}`,
      item.era       && `• Era: ${item.era}`,
      item.category  && `• Category: ${item.category}`,
      item.material  && `• Material: ${item.material}`,
      item.size      && `• Size: ${item.size}`,
      item.color     && `• Color: ${item.color}`,
      item.condition && `• Condition: ${item.condition}${item.conditionNotes ? ` — ${item.conditionNotes}` : ""}`,
      item.rarity    && `• Rarity: ${item.rarity}`,
    ].filter(Boolean).join("\n");

    if (m.includes("depop")) {
      return [
        titleDepop, "",
        story || `${item.era ?? ""} ${item.title}`.trim(), "",
        [item.size && `size ${item.size}`, item.color, item.material, item.condition && `${item.condition} condition`].filter(Boolean).join(" · "), "",
        `$${priceNum || item.pricing.suggested} · DM with questions, no holds 💌`,
        verifLine && `\n${verifLine}`,
        tagsHash && `\n${tagsHash}`,
      ].filter(Boolean).join("\n");
    }
    if (m.includes("grailed")) {
      return [
        titleGrailed, "",
        story && `${story}\n`,
        "DETAILS", detailsList, "",
        "MEASUREMENTS",
        measBlock || "• Pit-to-pit: __\"\n• Length: __\"\n• Shoulder: __\"\n• Sleeve: __\"", "",
        `Asking $${priceNum || item.pricing.suggested}. Offers welcome, lowballs ignored. Ships within 2 business days.`,
        verifLine && `\n${verifLine}`,
      ].filter(Boolean).join("\n");
    }
    if (m.includes("ebay")) {
      return [
        `Title (≤80): ${titleEbay}`, "",
        "ITEM SPECIFICS", detailsList, "",
        measBlock && `MEASUREMENTS\n${measBlock}\n`,
        "DESCRIPTION",
        story || `${item.brand ?? "Unbranded"} ${item.title} in ${item.condition ?? "good"} condition. ${item.era ? `${item.era} piece. ` : ""}Please review all photos as they form part of the description.`,
        "",
        `Buy It Now: $${priceNum || item.pricing.suggested}. Ships fast with tracking. Returns accepted within 30 days.`,
        verifLine && `\n${verifLine}`,
      ].filter(Boolean).join("\n");
    }
    if (m.includes("posh")) {
      return [
        titlePosh, "",
        story || `Lovely ${item.color ?? ""} ${item.title} from ${item.brand ?? "a great maker"}.`.trim(), "",
        detailsList,
        measBlock && `\nMEASUREMENTS\n${measBlock}`, "",
        `💲 Listed at $${priceNum || item.pricing.suggested} — bundle for a discount! Smoke-free closet, ships in 1–2 days.`,
        verifLine && `\n${verifLine}`,
      ].filter(Boolean).join("\n");
    }
    if (m.includes("vestiaire")) {
      return [
        titleVest, "",
        story || `Authentic ${item.brand ?? ""} ${item.title}${item.era ? `, ${item.era}` : ""}. ${item.condition ? `In ${item.condition.toLowerCase()} condition.` : ""}`.trim(), "",
        detailsList,
        measBlock && `\nMEASUREMENTS\n${measBlock}`, "",
        `Asking €/$${priceNum || item.pricing.suggested}. Authenticity will be verified by Vestiaire Collective.`,
        verifLine && `\n${verifLine}`,
      ].filter(Boolean).join("\n");
    }
    if (m.includes("etsy")) {
      return [
        titleEtsy, "",
        story || `A beautifully preserved ${item.era ?? "vintage"} ${item.title}${item.brand ? ` by ${item.brand}` : ""}. ${item.material ? `Crafted in ${item.material.toLowerCase()}.` : ""}`.trim(), "",
        "DETAILS", detailsList, "",
        measBlock ? `MEASUREMENTS\n${measBlock}` : "MEASUREMENTS — please convo before purchase if you'd like exact measurements.", "",
        `Priced at $${priceNum || item.pricing.suggested}. Carefully packaged and shipped within 3 business days from a smoke-free studio.`,
        verifLine && `\n${verifLine}`,
        tagsComma && `\nTags: ${tagsComma}`,
      ].filter(Boolean).join("\n");
    }
    return [
      titleDefault, "",
      `Price: ${priceStr}`, "",
      detailsList,
      measBlock && `\nMEASUREMENTS\n${measBlock}`,
      story && `\n${story}`,
      verifLine && `\n${verifLine}`,
      tagsHash && `\n${tagsHash}`,
    ].filter(Boolean).join("\n");
  }, [price, market, meas, item, verification]);

  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          width: "100%", display: "flex", alignItems: "center", gap: 12,
          background: open ? "hsl(var(--ink))" : "hsl(var(--cream))",
          color: open ? "hsl(var(--ink-fg))" : "hsl(var(--ink))",
          border: "1px solid hsl(var(--ink))",
          borderRadius: 22,
          padding: "14px 18px", cursor: "pointer",
          textAlign: "left",
          transition: "background 0.15s ease, color 0.15s ease",
        }}
      >
        <span style={{
          width: 36, height: 36, flexShrink: 0,
          borderRadius: 999,
          background: open ? "hsl(var(--cream))" : "hsl(var(--ink))",
          color: open ? "hsl(var(--ink))" : "hsl(var(--ink-fg))",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
        }}>
          {Icon.tag ? Icon.tag(16) : Icon.share(16)}
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{
            display: "block",
            fontFamily: "'Fraunces', 'Playfair Display', serif",
            fontWeight: 800, fontSize: 17, lineHeight: 1.1,
          }}>
            List it for sale
          </span>
          <span style={{
            display: "block", marginTop: 2,
            fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
            letterSpacing: "0.1em", textTransform: "uppercase",
            color: open ? "hsl(var(--cream))" : "hsl(var(--ink-2))",
            opacity: open ? 0.85 : 1,
          }}>
            Auto-write a marketplace draft
          </span>
        </span>
        <span style={{ flexShrink: 0, opacity: 0.7 }}>{Icon.chev(14, open ? "u" : "d")}</span>
      </button>

      {open && (
        <Card style={{ padding: 14, marginTop: 10, borderRadius: 22 }}>
          <div className="caps" style={{ marginBottom: 6 }}>Marketplace</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {marketOptions.map((mo) => {
              const active = market === mo;
              return (
                <button
                  key={mo}
                  onClick={() => setMarket(mo)}
                  style={{
                    padding: "6px 14px",
                    borderRadius: 999,
                    background: active ? "hsl(var(--ink))" : "transparent",
                    color: active ? "hsl(var(--ink-fg))" : "hsl(var(--ink))",
                    border: "1px solid hsl(var(--ink))", cursor: "pointer",
                    fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
                    letterSpacing: "0.1em", textTransform: "uppercase",
                  }}
                >
                  {mo}
                </button>
              );
            })}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 12 }}>
            <div style={{ flex: 1 }}>
              <div className="caps" style={{ marginBottom: 4 }}>Your price</div>
              <div style={{
                display: "inline-flex", alignItems: "center", gap: 4,
                border: "1px solid hsl(var(--ink))", borderRadius: 999, padding: "6px 14px",
                background: "hsl(var(--paper-3))",
              }}>
                <span style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 800, fontSize: 18, color: "hsl(var(--ink))" }}>$</span>
                <input
                  type="number"
                  inputMode="decimal"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  style={{
                    width: 80, border: "none", outline: "none", background: "transparent",
                    fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 800, fontSize: 18,
                    color: "hsl(var(--ink))",
                  }}
                />
              </div>
            </div>
            <div style={{
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
              color: "hsl(var(--ink-2))", letterSpacing: "0.06em", textAlign: "right",
            }}>
              Suggested ${item.pricing.suggested}<br />
              Range ${item.pricing.low}–${item.pricing.high}
            </div>
          </div>

          <div className="caps" style={{ marginTop: 14, marginBottom: 6 }}>
            Measurements <span style={{ textTransform: "none", letterSpacing: 0, color: "hsl(var(--ink-2))" }}>(inches, optional)</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
            {([
              ["pit",      "Pit-to-pit"],
              ["length",   "Length"],
              ["shoulder", "Shoulder"],
              ["sleeve",   "Sleeve"],
              ["waist",    "Waist"],
              ["hip",      "Hip"],
            ] as const).map(([key, label]) => (
              <label key={key} style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <span style={{
                  fontFamily: "'IBM Plex Mono', monospace", fontSize: 9,
                  letterSpacing: "0.08em", textTransform: "uppercase",
                  color: "hsl(var(--ink-2))",
                }}>{label}</span>
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="—"
                  value={meas[key]}
                  onChange={(e) => setMeas((mm) => ({ ...mm, [key]: e.target.value }))}
                  style={{
                    border: "1px solid hsl(var(--ink))", background: "hsl(var(--paper-3))",
                    borderRadius: 14,
                    padding: "6px 10px", outline: "none",
                    fontFamily: "'IBM Plex Mono', monospace", fontSize: 12,
                    color: "hsl(var(--ink))", width: "100%",
                  }}
                />
              </label>
            ))}
          </div>

          <div className="caps" style={{ marginTop: 14, marginBottom: 6 }}>Listing draft</div>
          <textarea
            readOnly
            value={generatedListing}
            style={{
              width: "100%", minHeight: 180, resize: "vertical",
              border: "1px dashed hsl(var(--ink-2))", borderRadius: 14, padding: 12,
              background: "hsl(var(--paper-3))", color: "hsl(var(--ink))",
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, lineHeight: 1.5,
            }}
          />

          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button
              onClick={async () => {
                try { await navigator.clipboard.writeText(generatedListing); toast.success("Listing copied"); }
                catch { toast.error("Couldn't copy"); }
              }}
              style={{
                flex: 1, background: "hsl(var(--ink))", color: "hsl(var(--ink-fg))",
                border: "1px solid hsl(var(--ink))", borderRadius: 999, padding: "12px", cursor: "pointer",
                fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
                letterSpacing: "0.12em", textTransform: "uppercase",
              }}
            >
              Copy listing
            </button>
            <button
              onClick={() => {
                const blob = new Blob([generatedListing], { type: "text/plain" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                const safe = (item.title || "listing").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
                a.href = url; a.download = `${safe || "listing"}-${market || "draft"}.txt`;
                document.body.appendChild(a); a.click();
                document.body.removeChild(a); URL.revokeObjectURL(url);
                toast.success("Exported .txt");
              }}
              style={{
                flex: 1, background: "transparent", color: "hsl(var(--ink))",
                border: "1px solid hsl(var(--ink))", borderRadius: 999, padding: "12px", cursor: "pointer",
                fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
                letterSpacing: "0.12em", textTransform: "uppercase",
              }}
            >
              Export
            </button>
          </div>
        </Card>
      )}
    </>
  );
}